import {
  resolveMutationErrorKey,
  type MutationErrorLike,
} from "@/partner/shared/lib";

/** Коды отказов API рассылок (текст сообщения UserInputError/ForbiddenError). */
export type MarketingErrorCode =
  | "EMAIL_TEMPLATE_NAME_REQUIRED"
  | "EMAIL_TEMPLATE_NAME_TOO_LONG"
  | "EMAIL_TEMPLATE_VARIANT_REQUIRED"
  | "EMAIL_TEMPLATE_DUPLICATE_LANGUAGE"
  | "EMAIL_TEMPLATE_SUBJECT_REQUIRED"
  | "EMAIL_TEMPLATE_SUBJECT_TOO_LONG"
  | "EMAIL_TEMPLATE_PREHEADER_TOO_LONG"
  | "EMAIL_TEMPLATE_TOO_MANY_BLOCKS"
  | "EMAIL_TEMPLATE_DUPLICATE_BLOCK_ID"
  | "EMAIL_TEMPLATE_BLOCK_INCOMPLETE"
  | "EMAIL_TEMPLATE_TEXT_TOO_LONG"
  | "EMAIL_TEMPLATE_URL_INVALID"
  | "MARKETING_EMAILS_DISABLED"
  | "MARKETING_FREQUENCY_CAP_INVALID"
  | "MARKETING_TEST_SEND_LIMIT"
  | "MARKETING_SENDING_DISABLED"
  | "MARKETING_TEST_EMAIL_REQUIRED"
  // Рассылки: жизненный цикл и оценка аудитории (фаза 2, Task 8/11).
  | "AUDIENCE_FILTER_INVALID"
  | "BROADCAST_NOT_FOUND"
  | "BROADCAST_NOT_EDITABLE"
  | "BROADCAST_ALREADY_STARTED"
  | "BROADCAST_EMAIL_REQUIRED"
  | "BROADCAST_EMAIL_NOT_FOUND"
  | "BROADCAST_AUDIENCE_REQUIRED"
  | "BROADCAST_SCHEDULE_IN_PAST"
  | "CONTACT_LIST_NOT_FOUND"
  | "AUTOMATION_TEMPLATE_NOT_FOUND"
  | "AUTOMATION_NOT_DELETABLE"
  | "EMAIL_TEMPLATE_IN_USE"
  | "CONTACT_LIST_NAME_REQUIRED"
  | "CONTACT_CONSENT_REQUIRED"
  | "CONTACT_IMPORT_TOO_LARGE"
  // Название и списки контактов рассылки (Task 16 фикс-волна).
  | "BROADCAST_NAME_REQUIRED"
  | "BROADCAST_NAME_TOO_LONG"
  | "TOO_MANY_CONTACT_LISTS"
  // Автоматизации по событиям (фаза 3).
  | "AUTOMATION_NOT_FOUND"
  | "AUTOMATION_NOT_EDITABLE"
  // Сохранение от устаревшей версии (финальное ревью I2): другое окно
  // сохранило автоматизацию раньше.
  | "AUTOMATION_REVISION_CONFLICT"
  | "AUTOMATION_KIND_NOT_SUPPORTED"
  | "AUTOMATION_GRAPH_MALFORMED"
  | "AUTOMATION_NAME_REQUIRED"
  | "AUTOMATION_NAME_TOO_LONG"
  // Цепочки (фаза 4): правила выхода, повторный вход, судьба зрителей
  // удалённых шагов.
  | "AUTOMATION_EXIT_RULES_MALFORMED"
  | "AUTOMATION_REENTRY_INVALID"
  | "AUTOMATION_NODE_MOVES_MALFORMED"
  // Потолки объёма платформы (ревью безопасности 2026-10-01, пункт E).
  | "AUTOMATION_LIMIT_REACHED"
  | "ACTIVE_AUTOMATION_LIMIT_REACHED"
  | "EMAIL_TEMPLATE_LIMIT_REACHED"
  | "CONTACT_LIST_LIMIT_REACHED"
  | "CONTACT_LIMIT_REACHED";

export const MARKETING_ERROR_KEYS: Record<MarketingErrorCode, string> = {
  EMAIL_TEMPLATE_NAME_REQUIRED: "marketingErrors.nameRequired",
  EMAIL_TEMPLATE_NAME_TOO_LONG: "marketingErrors.nameTooLong",
  EMAIL_TEMPLATE_VARIANT_REQUIRED: "marketingErrors.variantRequired",
  EMAIL_TEMPLATE_DUPLICATE_LANGUAGE: "marketingErrors.duplicateLanguage",
  EMAIL_TEMPLATE_SUBJECT_REQUIRED: "marketingErrors.subjectRequired",
  EMAIL_TEMPLATE_SUBJECT_TOO_LONG: "marketingErrors.subjectTooLong",
  EMAIL_TEMPLATE_PREHEADER_TOO_LONG: "marketingErrors.preheaderTooLong",
  EMAIL_TEMPLATE_TOO_MANY_BLOCKS: "marketingErrors.tooManyBlocks",
  EMAIL_TEMPLATE_DUPLICATE_BLOCK_ID: "marketingErrors.duplicateBlockId",
  EMAIL_TEMPLATE_BLOCK_INCOMPLETE: "marketingErrors.blockIncomplete",
  EMAIL_TEMPLATE_TEXT_TOO_LONG: "marketingErrors.textTooLong",
  EMAIL_TEMPLATE_URL_INVALID: "marketingErrors.urlInvalid",
  MARKETING_EMAILS_DISABLED: "marketingErrors.emailsDisabled",
  MARKETING_FREQUENCY_CAP_INVALID: "marketingErrors.frequencyCapInvalid",
  MARKETING_TEST_SEND_LIMIT: "marketingErrors.testSendLimit",
  MARKETING_SENDING_DISABLED: "marketingErrors.MARKETING_SENDING_DISABLED",
  MARKETING_TEST_EMAIL_REQUIRED: "marketingErrors.testEmailRequired",
  AUDIENCE_FILTER_INVALID: "marketingErrors.AUDIENCE_FILTER_INVALID",
  BROADCAST_NOT_FOUND: "marketingErrors.BROADCAST_NOT_FOUND",
  BROADCAST_NOT_EDITABLE: "marketingErrors.BROADCAST_NOT_EDITABLE",
  BROADCAST_ALREADY_STARTED: "marketingErrors.BROADCAST_ALREADY_STARTED",
  BROADCAST_EMAIL_REQUIRED: "marketingErrors.BROADCAST_EMAIL_REQUIRED",
  BROADCAST_EMAIL_NOT_FOUND: "marketingErrors.BROADCAST_EMAIL_NOT_FOUND",
  BROADCAST_AUDIENCE_REQUIRED: "marketingErrors.BROADCAST_AUDIENCE_REQUIRED",
  BROADCAST_SCHEDULE_IN_PAST: "marketingErrors.BROADCAST_SCHEDULE_IN_PAST",
  CONTACT_LIST_NOT_FOUND: "marketingErrors.CONTACT_LIST_NOT_FOUND",
  AUTOMATION_TEMPLATE_NOT_FOUND:
    "marketingErrors.AUTOMATION_TEMPLATE_NOT_FOUND",
  AUTOMATION_NOT_DELETABLE: "marketingErrors.AUTOMATION_NOT_DELETABLE",
  EMAIL_TEMPLATE_IN_USE: "marketingErrors.EMAIL_TEMPLATE_IN_USE",
  CONTACT_LIST_NAME_REQUIRED: "marketingErrors.CONTACT_LIST_NAME_REQUIRED",
  CONTACT_CONSENT_REQUIRED: "marketingErrors.CONTACT_CONSENT_REQUIRED",
  CONTACT_IMPORT_TOO_LARGE: "marketingErrors.CONTACT_IMPORT_TOO_LARGE",
  BROADCAST_NAME_REQUIRED: "marketingErrors.BROADCAST_NAME_REQUIRED",
  BROADCAST_NAME_TOO_LONG: "marketingErrors.BROADCAST_NAME_TOO_LONG",
  TOO_MANY_CONTACT_LISTS: "marketingErrors.TOO_MANY_CONTACT_LISTS",
  AUTOMATION_NOT_FOUND: "marketingErrors.AUTOMATION_NOT_FOUND",
  AUTOMATION_NOT_EDITABLE: "marketingErrors.AUTOMATION_NOT_EDITABLE",
  AUTOMATION_REVISION_CONFLICT: "marketingErrors.AUTOMATION_REVISION_CONFLICT",
  AUTOMATION_KIND_NOT_SUPPORTED:
    "marketingErrors.AUTOMATION_KIND_NOT_SUPPORTED",
  AUTOMATION_GRAPH_MALFORMED: "marketingErrors.AUTOMATION_GRAPH_MALFORMED",
  AUTOMATION_NAME_REQUIRED: "marketingErrors.AUTOMATION_NAME_REQUIRED",
  AUTOMATION_NAME_TOO_LONG: "marketingErrors.AUTOMATION_NAME_TOO_LONG",
  AUTOMATION_EXIT_RULES_MALFORMED:
    "marketingErrors.AUTOMATION_EXIT_RULES_MALFORMED",
  AUTOMATION_REENTRY_INVALID: "marketingErrors.AUTOMATION_REENTRY_INVALID",
  AUTOMATION_NODE_MOVES_MALFORMED:
    "marketingErrors.AUTOMATION_NODE_MOVES_MALFORMED",
  AUTOMATION_LIMIT_REACHED: "marketingErrors.AUTOMATION_LIMIT_REACHED",
  ACTIVE_AUTOMATION_LIMIT_REACHED:
    "marketingErrors.ACTIVE_AUTOMATION_LIMIT_REACHED",
  EMAIL_TEMPLATE_LIMIT_REACHED: "marketingErrors.EMAIL_TEMPLATE_LIMIT_REACHED",
  CONTACT_LIST_LIMIT_REACHED: "marketingErrors.CONTACT_LIST_LIMIT_REACHED",
  CONTACT_LIMIT_REACHED: "marketingErrors.CONTACT_LIMIT_REACHED",
};

export const MARKETING_ERROR_FALLBACK_KEY = "marketingErrors.unknown";

/** Ключ перевода для ошибки мутации рассылок; сырой message не показываем. */
export function marketingErrorKey(
  error: MutationErrorLike | null | undefined
): string {
  return resolveMutationErrorKey(
    error,
    MARKETING_ERROR_KEYS,
    MARKETING_ERROR_FALLBACK_KEY
  );
}
